import React, { useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  Avatar,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import { format } from "date-fns";

const ChatUsersDetails = ({ open, onClose, chatUsers }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 12;

  // Deduplicate users by email and get the most recent entry for each user
  const uniqueUsers = useMemo(() => {
    if (!chatUsers?.length) return [];

    const userMap = new Map();

    chatUsers.forEach((user) => {
      const email = user.email?.toLowerCase() || "no-email";
      const existingUser = userMap.get(email);

      // Keep the most recent user entry (or the one with more complete data)
      if (
        !existingUser ||
        new Date(user.createdAt) > new Date(existingUser.createdAt) ||
        (user.name && !existingUser.name)
      ) {
        userMap.set(email, user);
      }
    });

    return Array.from(userMap.values());
  }, [chatUsers]);

  // Filter users based on search term
  const filteredUsers = useMemo(() => {
    if (!searchTerm) return uniqueUsers;

    const term = searchTerm.toLowerCase();
    return uniqueUsers.filter(
      (user) =>
        (user.name && user.name.toLowerCase().includes(term)) ||
        (user.email && user.email.toLowerCase().includes(term))
    );
  }, [uniqueUsers, searchTerm]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex = (currentPage - 1) * usersPerPage;
    return filteredUsers.slice(startIndex, startIndex + usersPerPage);
  }, [filteredUsers, currentPage]);

  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Reset search and pagination when modal closes
  const handleClose = () => {
    setSearchTerm("");
    setCurrentPage(1);
    onClose();
  };

  const getInitials = (name) => {
    if (!name || name === "—") return "?";
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getStatusColor = (createdAt) => {
    const daysSinceCreated = Math.floor(
      (new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceCreated <= 7) return "success";
    if (daysSinceCreated <= 30) return "warning";
    return "default";
  };

  const getStatusText = (createdAt) => {
    const daysSinceCreated = Math.floor(
      (new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceCreated <= 7) return "New User";
    if (daysSinceCreated <= 30) return "Recent User";
    return "Established User";
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 2 },
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between", pr: 2 }}
      >
        <Typography variant="h6" component="span">
          👥 Chat Users Details
        </Typography>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "#f9f9f9" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Summary Stats */}
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              📊 Summary
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Total Users
                </Typography>
                <Typography variant="h5">{uniqueUsers.length}</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  New Users (7 days)
                </Typography>
                <Typography variant="h5">
                  {
                    uniqueUsers.filter(
                      (user) =>
                        Math.floor(
                          (new Date() - new Date(user.createdAt)) /
                            (1000 * 60 * 60 * 24)
                        ) <= 7
                    ).length
                  }
                </Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2" color="text.secondary">
                  Recent Users (30 days)
                </Typography>
                <Typography variant="h5">
                  {
                    uniqueUsers.filter(
                      (user) =>
                        Math.floor(
                          (new Date() - new Date(user.createdAt)) /
                            (1000 * 60 * 60 * 24)
                        ) <= 30
                    ).length
                  }
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          {/* Users List */}
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                👤 All Users ({filteredUsers.length} of {uniqueUsers.length})
              </Typography>
              <TextField
                size="small"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1); // Reset to first page when searching
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: 250 }}
              />
            </Box>

            <Grid container spacing={2}>
              {paginatedUsers.map((user, index) => (
                <Grid item xs={12} sm={6} md={4} key={user.id || index}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        transform: "translateY(-2px)",
                        boxShadow: 2,
                      },
                    }}
                  >
                    <Avatar sx={{ bgcolor: "primary.main" }}>
                      {getInitials(user.name)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {user.name || "Anonymous"}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                      >
                        {user.email || "No email"}
                      </Typography>
                      <Box sx={{ mt: 1 }}>
                        <Chip
                          size="small"
                          label={getStatusText(user.createdAt)}
                          color={getStatusColor(user.createdAt)}
                          variant="outlined"
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(event, page) => setCurrentPage(page)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}

            {/* Empty States */}
            {filteredUsers.length === 0 && searchTerm && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <SearchIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No users found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Try adjusting your search terms
                </Typography>
              </Box>
            )}

            {uniqueUsers.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <PersonIcon
                  sx={{ fontSize: 48, color: "text.secondary", mb: 2 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No users found
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Users will appear here once they start chatting
                </Typography>
              </Box>
            )}
          </Paper>

          {/* Recent Activity */}
          <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              🕒 Recent Activity
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: "auto" }}>
              {uniqueUsers
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 10)
                .map((user, index) => (
                  <Box
                    key={user.id || index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1,
                      borderBottom: index < 9 ? "1px solid #eee" : "none",
                    }}
                  >
                    <Avatar
                      sx={{ width: 32, height: 32, bgcolor: "primary.main" }}
                    >
                      {getInitials(user.name)}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2">
                        <strong>{user.name || "Anonymous"}</strong> joined
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {format(new Date(user.createdAt), "MMM dd, yyyy HH:mm")}
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={getStatusText(user.createdAt)}
                      color={getStatusColor(user.createdAt)}
                      variant="outlined"
                    />
                  </Box>
                ))}
            </Box>
          </Paper>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChatUsersDetails;
